import { CheckCircle2, FileUp, Trash2 } from 'lucide-react'
import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import { uploadAdmissionDocument } from '../api'
import type { RequiredDocument } from '../types'
import { formatBytes, type UploadedDoc } from '../wizard'

const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'

/** One requested document: choose a file, watch it upload, replace or remove it. */
export function DocumentUpload({
  doc,
  value,
  onChange,
  maxBytes,
  types,
  error,
}: {
  doc: RequiredDocument
  value: UploadedDoc | undefined
  onChange: (value: UploadedDoc | undefined) => void
  maxBytes: number
  types: string[]
  error?: string
}) {
  const { t } = useTranslation('admissions')
  const id = useId()
  const input = useRef<HTMLInputElement>(null)
  const [progress, setProgress] = useState<number | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const message = failure ?? error ?? null

  const pick = async (file: File | undefined) => {
    if (!file) return
    setFailure(null)
    if (file.size > maxBytes) {
      setFailure(
        t('The file may not be larger than {{size}} MB.', { size: Math.round(maxBytes / 1024 / 1024) }),
      )
      return
    }
    if (file.type && !types.includes(file.type)) {
      setFailure(t('Send a PDF, JPEG or PNG file.'))
      return
    }
    setProgress(0)
    try {
      const uploaded = await uploadAdmissionDocument(file, setProgress)
      onChange({
        token: uploaded.token,
        name: uploaded.name,
        size: uploaded.size,
        expiresAt: uploaded.expiresAt,
      })
    } catch (e) {
      setFailure(toApiError(e).message)
    } finally {
      setProgress(null)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div
      className={cn(
        'rounded-control border bg-surface p-3.5',
        message ? 'border-danger-500/50' : value ? 'border-success-500/40' : 'border-line',
      )}
    >
      <div className="flex min-w-0 flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p id={`${id}-label`} className="text-sm font-medium break-words text-ink">
            {doc.label}
          </p>
          {doc.description ? <p className="mt-0.5 text-xs text-muted">{doc.description}</p> : null}
        </div>
        <Badge tone={doc.required ? 'warning' : 'neutral'} size="sm">
          {doc.required ? t('Required') : t('Optional')}
        </Badge>
      </div>

      {value ? (
        <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2 text-sm">
          <CheckCircle2 className="size-4 shrink-0 text-success-600" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate" title={value.name}>
            {value.name}
          </span>
          <span className="text-xs text-muted">{formatBytes(value.size)}</span>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Trash2 />}
            onClick={() => onChange(undefined)}
            aria-label={t('Remove {{name}}', { name: value.name })}
          >
            {t('Remove')}
          </Button>
        </div>
      ) : null}

      {progress !== null ? (
        <div className="mt-3" role="status">
          <div
            className="h-2 overflow-hidden rounded-full bg-surface-sunken"
            role="progressbar"
            aria-labelledby={`${id}-label`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
          >
            <div
              className="h-full bg-brand-600 transition-[width]"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-muted">
            {t('Uploading… {{percent}}%', { percent: Math.round(progress * 100) })}
          </p>
        </div>
      ) : (
        <div className="mt-3">
          <input
            ref={input}
            id={`${id}-file`}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            aria-describedby={message ? `${id}-error` : `${id}-hint`}
            aria-invalid={message ? true : undefined}
            onChange={(e) => void pick(e.target.files?.[0])}
          />
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<FileUp />}
            className="h-auto min-h-8 py-1.5 text-left leading-snug whitespace-normal"
            onClick={() => input.current?.click()}
            aria-label={
              value
                ? t('Replace {{name}}', { name: doc.label })
                : t('Choose a file for {{name}}', { name: doc.label })
            }
          >
            {value ? t('Replace the file') : t('Choose a file')}
          </Button>
          <p id={`${id}-hint`} className="mt-1 text-xs text-muted">
            {t('PDF, JPEG or PNG, {{size}} MB at most.', { size: Math.round(maxBytes / 1024 / 1024) })}
          </p>
        </div>
      )}
      {message ? (
        <p id={`${id}-error`} role="alert" className="mt-2 text-xs font-medium text-danger-600">
          {t(message)}
        </p>
      ) : null}
    </div>
  )
}
